import { Box, Grid, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { format, isBefore, subMonths } from 'date-fns';
import { useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import AssetIcon from '../../../shared/AssetIcon';

const COLUMN_FLEX = {
  SMALL: 2 / 3,
  MEDIUM: 1,
  LARGE: 2,
};

const COLORS = {
  POSITIVE: '#4caf50',
  NEGATIVE: '#f44336',
  BACKGROUND: '#f8f9fa',
  SURFACE: '#ffffff',
  BORDER: '#e8eaed',
  TEXT_PRIMARY: '#202124',
};

const SPACING = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
};

const DELTA_WEIGHT_THRESHOLD = 0.1;
const SHARES_RECENT_MONTHS = 1;

const isRecentUpdate = (dateString) => {
  return isBefore(
    subMonths(new Date(), SHARES_RECENT_MONTHS),
    new Date(dateString),
  );
};

export default function PositionsComponent({ positions, updateRow }) {
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });

  const buildColumns = useMemo(
    () => [
      {
        field: 'symbol',
        headerName: 'Symbol',
        flex: COLUMN_FLEX.MEDIUM,
        renderCell: ({ row, formattedValue }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssetIcon
              iconUrl={row.iconUrl}
              label={row.symbol || row.companyName}
              variant="rounded"
              size={24}
            />
            {!isMobile && <span>{formattedValue}</span>}
          </Box>
        ),
      },
      {
        field: 'companyName',
        headerName: 'Company',
        flex: COLUMN_FLEX.LARGE,
      },
      {
        field: 'averagePrice',
        headerName: 'Avg. Price',
        type: 'number',
        flex: COLUMN_FLEX.SMALL,
        valueGetter: (_, row) => row.positionState.averagePrice,
        renderCell: ({ row }) => {
          return (
            <Typography variant="body">
              {`${Number(row.positionState.averagePrice).toFixed(2)}`}
            </Typography>
          );
        },
      },
      {
        field: 'price',
        headerName: 'Price',
        type: 'number',
        flex: COLUMN_FLEX.SMALL,
        valueGetter: (_, row) => row.companyState.price,
        renderCell: ({ row }) => {
          return (
            <Typography variant="body">
              {`${Number(row.companyState.price).toFixed(2)}`}
            </Typography>
          );
        },
      },
      {
        field: 'value',
        headerName: 'Value',
        type: 'number',
        flex: 1,
        renderCell: ({ row }) => (
          <Typography variant="body">
            {Number(row.positionState.value).toFixed(2)}
          </Typography>
        ),
      },
      {
        field: 'currency',
        headerName: 'Currency',
        type: 'string',
        flex: COLUMN_FLEX.SMALL,
        renderCell: ({ row }) => (
          <Typography variant="body">{row?.positionState.currency}</Typography>
        ),
      },
      {
        field: 'currentWeight',
        headerName: 'Current',
        type: 'number',
        flex: COLUMN_FLEX.SMALL,
      },
      {
        field: 'targetWeight',
        headerName: 'Target',
        type: 'number',
        editable: true,
        flex: COLUMN_FLEX.SMALL,
      },
      {
        field: 'deltaWeight',
        headerName: 'Delta weight',
        type: 'number',
        flex: COLUMN_FLEX.MEDIUM,
        valueGetter: (_, row) => row?.deltaWeight,
        renderCell: ({ row }) => {
          const color = row.deltaWeight < 0 ? COLORS.NEGATIVE : COLORS.POSITIVE;
          const fontWeight =
            Math.abs(row.deltaWeight) > DELTA_WEIGHT_THRESHOLD
              ? 'bold'
              : 'medium';
          return (
            <Typography variant="body" color={color} fontWeight={fontWeight}>
              {`${row.deltaWeight}`}
            </Typography>
          );
        },
      },
      {
        field: 'deltaShares',
        headerName: 'Delta shares',
        type: 'number',
        flex: COLUMN_FLEX.MEDIUM,
        valueGetter: (_, row) => row?.deltaShares * -1,
        renderCell: ({ row }) => {
          const value = row?.deltaShares * -1;
          const color = value < 0 ? COLORS.NEGATIVE : COLORS.POSITIVE;
          return (
            <Typography variant="body" color={color}>
              {value}
            </Typography>
          );
        },
      },
      {
        field: 'shares',
        headerName: 'Shares',
        type: 'number',
        flex: COLUMN_FLEX.SMALL,
        valueGetter: (_, row) => row.positionState.shares,
        renderCell: ({ row, value }) => {
          const color = isRecentUpdate(row.sharesUpdatedAt)
            ? COLORS.NEGATIVE
            : COLORS.POSITIVE;
          return (
            <Typography variant="body" color={color}>
              {value}
            </Typography>
          );
        },
      },
      {
        field: 'sharesUpdatedAt',
        headerName: 'Last change',
        type: 'date',
        flex: COLUMN_FLEX.SMALL,
        valueGetter: (_, row) =>
          row.sharesUpdatedAt ? new Date(row.sharesUpdatedAt) : null,
        renderCell: ({ row }) => {
          const color = isRecentUpdate(row.sharesUpdatedAt)
            ? COLORS.NEGATIVE
            : COLORS.POSITIVE;
          return (
            <Typography variant="body" color={color}>
              {format(new Date(row.sharesUpdatedAt), 'dd/MM/yyyy')}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: SPACING.MD,
        px: isMobile ? 0 : SPACING.MD,
        backgroundColor: COLORS.BACKGROUND,
        borderRadius: isMobile ? 0 : '8px',
      }}
    >
      <Grid item xs={12} padding={isMobile ? 0 : SPACING.XS}>
        <Box
          sx={{
            backgroundColor: COLORS.SURFACE,
            borderRadius: '8px',
            border: `1px solid ${COLORS.BORDER}`,
            overflow: 'hidden',
          }}
        >
          <DataGrid
            autoHeight
            rows={positions}
            columns={buildColumns}
            columnVisibilityModel={{
              companyName: !isMobile,
              averagePrice: !isMobile,
              currency: !isMobile,
              sharesUpdatedAt: !isMobile,
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'deltaWeight', sort: 'asc' }],
              },
              pagination: {
                paginationModel,
              },
            }}
            density="compact"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 20, 50]}
            processRowUpdate={updateRow}
            sx={{
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: '#f9f9f9',
                },
              },
            }}
          />
        </Box>
      </Grid>
    </Box>
  );
}
